import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  useMyRequests,
  useMyRequestDetail,
  useMarkRead,
  useSendMyReply,
  useArchiveConversation,
  type MyRequest,
  type MyReply,
} from '../hooks/useMySupportRequests';
import {
  FiMessageSquare,
  FiPlus,
  FiArrowLeft,
  FiArchive,
  FiInbox,
  FiSend,
  FiCheck,
  FiLoader,
  FiImage,
  FiX,
} from 'react-icons/fi';
import gLogoViolet from '@/assets/g_logo_violet.png';
import { BrandedSpinner } from '@/components/BrandedSpinner';
import { motion } from 'framer-motion';
import { getUserFromClerkId } from '@/hooks/useUser';
import AvatarDisplay from '@/components/AvatarDisplay';
import LinkifiedText from '@/components/chat/LinkifiedText';

export const Route = createFileRoute('/_auth/messages')({
  component: MessagesPage,
});

// ── Constants ───────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700',
  IN_PROGRESS: 'bg-blue-50 text-blue-700',
  RESOLVED: 'bg-emerald-50 text-emerald-700',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Awaiting reply',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
};

// ── Helpers ─────────────────────────────────────────────────

function timeAgo(dateString: string) {
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
}

function formatTime(dateString: string) {
  const d = new Date(dateString);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (isToday) return time;
  if (isYesterday) return `Yesterday, ${time}`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) + `, ${time}`;
}

// ── Main page ───────────────────────────────────────────────

function MessagesPage() {
  const { data: requests, isLoading } = useMyRequests();
  const archiveMutation = useArchiveConversation();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);

  // Auto-select the first thread on desktop when requests load
  useEffect(() => {
    if (!selectedThreadId && requests && requests.length > 0) {
      if (window.innerWidth >= 768) {
        setSelectedThreadId(requests[0].id);
      }
    }
  }, [requests, selectedThreadId]);

  return (
    <div className="h-[calc(100dvh-72px)] md:h-[calc(100dvh-80px)] overflow-hidden bg-white">
      <div className="mx-auto flex h-full max-w-6xl">

        {/* ── Sidebar: conversation list ── */}
        <aside
          className={`flex flex-col border-r border-slate-200/60 bg-white transition-all ${
            selectedThreadId
              ? 'hidden md:flex md:w-[340px] xl:w-[380px] shrink-0'
              : 'w-full md:w-[340px] xl:w-[380px] shrink-0'
          }`}
        >
          {/* Header */}
          <div className="shrink-0 border-b border-slate-100 px-5 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-semibold text-slate-900">Messages</h1>
              <Link
                to="/contact-support"
                className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700"
              >
                <FiPlus className="h-4 w-4" />
                New
              </Link>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="animate-pulse">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="border-b border-slate-100/80 px-5 py-3.5">
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 shrink-0 rounded-full bg-slate-100" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3">
                          <div className="h-4 rounded bg-slate-100" style={{ width: `${55 + (i * 17) % 30}%` }} />
                          <div className="h-4 w-16 shrink-0 rounded-full bg-slate-100" />
                        </div>
                        <div className="mt-2 h-3 w-2/5 rounded bg-slate-100/70" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isLoading && (!requests || requests.length === 0) && (
              <div className="flex flex-col items-center justify-center px-6 py-20">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                  <FiInbox className="h-6 w-6 text-slate-400" />
                </div>
                <h2 className="text-base font-semibold text-slate-900">No messages yet</h2>
                <p className="mt-1 text-sm text-slate-500 text-center">
                  When you contact support, your conversations will appear here.
                </p>
                <Link
                  to="/contact-support"
                  className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-violet-700 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-violet-800"
                >
                  <FiPlus className="h-4 w-4" />
                  Contact support
                </Link>
              </div>
            )}

            {requests?.map((req) => (
              <SidebarItem
                key={req.id}
                request={req}
                isActive={req.id === selectedThreadId}
                onClick={() => setSelectedThreadId(req.id)}
                onArchive={() => {
                  archiveMutation.mutate(req.id);
                  if (selectedThreadId === req.id) setSelectedThreadId(null);
                }}
              />
            ))}
          </div>
        </aside>

        {/* ── Main content: chat or empty state ── */}
        <main
          className={`flex flex-1 flex-col min-w-0 bg-white ${
            selectedThreadId ? '' : 'hidden md:flex'
          }`}
        >
          {selectedThreadId ? (
            <ChatPanel
              threadId={selectedThreadId}
              onBack={() => setSelectedThreadId(null)}
            />
          ) : (
            <EmptyState />
          )}
        </main>
      </div>
    </div>
  );
}

// ── Sidebar item ────────────────────────────────────────────

function SidebarItem({
  request: req,
  isActive,
  onClick,
  onArchive,
}: {
  request: MyRequest;
  isActive: boolean;
  onClick: () => void;
  onArchive: () => void;
}) {
  const hasUnread = isActive
    ? false
    : req.userLastReadAt
      ? new Date(req.updatedAt) > new Date(req.userLastReadAt)
      : (req._count?.replies ?? 0) > 0 || req.type === 'DIRECT_MESSAGE';

  return (
    <div
      className={`relative group block w-full text-left border-b border-slate-100/80 px-5 py-3.5 transition-colors cursor-pointer ${
        isActive
          ? 'bg-violet-50/60 border-l-2 border-l-violet-700'
          : 'hover:bg-slate-50 border-l-2 border-l-transparent'
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      {/* Archive button — hover only */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onArchive(); }}
        className="absolute top-2 right-2 hidden group-hover:flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer z-10"
        aria-label="Archive conversation"
        title="Archive"
      >
        <FiArchive className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-3">
        <div className="relative shrink-0 mt-0.5">
          {req.type === 'DIRECT_MESSAGE' && req.senderAvatarUrl ? (
            <img src={req.senderAvatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <img src={gLogoViolet} alt="" className="h-8 w-8" />
          )}
          {hasUnread && !isActive && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-white" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p
              className={`text-[15px] truncate ${isActive ? 'text-violet-700 font-medium' : hasUnread ? 'text-slate-900 font-semibold' : 'text-slate-800 font-medium'}`}
            >
              {req.subject}
            </p>
            {req.type !== 'DIRECT_MESSAGE' && (
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLES[req.status] ?? ''}`}
              >
                {STATUS_LABELS[req.status] ?? req.status}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
            <span>{req.type === 'DIRECT_MESSAGE' ? (req.senderDisplayName || 'Direct message') : req.referenceId}</span>
            <span>&middot;</span>
            <span>{timeAgo(req.updatedAt)}</span>
            {(req._count?.replies ?? 0) > 0 && (
              <>
                <span>&middot;</span>
                <FiMessageSquare className="h-2.5 w-2.5" />
                <span>{req._count?.replies}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Empty state (no thread selected) ────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center px-6">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
        <FiMessageSquare className="h-7 w-7 text-slate-300" />
      </div>
      <h2 className="text-lg font-semibold text-slate-700">Select a conversation</h2>
      <p className="mt-1.5 text-sm text-slate-400 max-w-xs">
        Choose a message from the sidebar to view the conversation.
      </p>
    </div>
  );
}

// ── Chat panel ──────────────────────────────────────────────

function ChatPanel({ threadId, onBack }: { threadId: string; onBack: () => void }) {
  const { user: clerkUser } = useUser();
  const { data: userData } = getUserFromClerkId(clerkUser?.id ?? '', { enabled: !!clerkUser?.id });
  const { data: ticket, isLoading } = useMyRequestDetail(threadId);
  const sendReply = useSendMyReply();
  const markRead = useMarkRead();
  const [message, setMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const userEmail = ticket?.email ?? '';
  // Prefer the GranGuard profile name over Clerk's name
  const userName = userData?.name || clerkUser?.firstName || clerkUser?.fullName || userEmail;

  // Mark thread as read when opened or when new replies arrive
  useEffect(() => {
    if (ticket) {
      markRead.mutate(threadId);
    }
  }, [threadId, ticket?.replies?.length]);

  // Auto-scroll chat container to bottom (without moving the outer page)
  const scrollToBottom = useCallback((instant?: boolean) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: instant ? 'instant' : 'smooth',
      });
    }
  }, []);

  useLayoutEffect(() => {
    scrollToBottom(true);
  }, [threadId]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.replies?.length, scrollToBottom]);

  // Reset message when switching threads
  useEffect(() => {
    setMessage('');
    setSelectedImage(null);
    setImagePreview(null);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [threadId]);

  // Clean up image preview URL
  useEffect(() => {
    if (!selectedImage) {
      setImagePreview(null);
      return;
    }
    const url = URL.createObjectURL(selectedImage);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedImage]);

  // Auto-resize textarea
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  const handleSend = () => {
    if ((!message.trim() && !selectedImage) || sendReply.isPending) return;
    sendReply.mutate(
      { supportRequestId: threadId, body: message.trim(), image: selectedImage || undefined },
      {
        onSuccess: () => {
          setMessage('');
          setSelectedImage(null);
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
          }
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <BrandedSpinner size="lg" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-slate-400">Request not found</p>
      </div>
    );
  }

  const isDM = ticket.type === 'DIRECT_MESSAGE';
  // For DMs, prefer the cached sender display name but allow individual replies
  // to override it (sentByName is set fresh from the admin's current profile name)
  const dmFallbackName = ticket.senderDisplayName || 'GranGuard';

  const classifiedMessages = [
    {
      id: 'original',
      body: ticket.description,
      imageUrl: undefined as string | null | undefined,
      fromUser: !ticket.initiatedByAdmin,
      senderName: ticket.initiatedByAdmin ? dmFallbackName : userName,
      createdAt: ticket.createdAt,
    },
    ...(ticket.replies ?? []).map((r: MyReply) => ({
      id: r.id,
      body: r.body,
      imageUrl: r.imageUrl,
      fromUser: r.fromUser ?? false,
      // For staff replies: prefer sentByName (fresh from profile) over cached senderDisplayName
      senderName: r.fromUser ? userName : (r.sentByName || (isDM ? dmFallbackName : 'GranGuard Support')),
      createdAt: r.createdAt,
    })),
  ];

  return (
    <>
      {/* Chat header */}
      <div className="shrink-0 border-b border-slate-200/60 bg-white px-5 py-3.5 sm:px-6">
        <div className="flex items-center gap-3">
          {/* Back button (mobile only) */}
          <button
            type="button"
            onClick={onBack}
            className="md:hidden flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors cursor-pointer"
          >
            <FiArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold text-slate-900 truncate">{ticket.subject}</h1>
            <p className="text-xs text-slate-400 mt-0.5">
              {!isDM && <>{ticket.referenceId} &middot; </>}Started{' '}
              {new Date(ticket.createdAt).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </p>
          </div>
          {!isDM && (
            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${STATUS_STYLES[ticket.status] ?? ''}`}
            >
              {STATUS_LABELS[ticket.status] ?? ticket.status}
            </span>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain bg-white">
        <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
          <div className="space-y-3">
            {classifiedMessages.map((msg, i) => {
              const prevDate =
                i > 0 ? new Date(classifiedMessages[i - 1].createdAt).toDateString() : null;
              const thisDate = new Date(msg.createdAt).toDateString();
              const showDateSep = prevDate !== thisDate;
              const prevFromUser = i > 0 ? classifiedMessages[i - 1].fromUser : null;
              const isFirstInGroup = prevFromUser !== msg.fromUser || showDateSep;

              return (
                <div key={msg.id}>
                  {showDateSep && <DateSeparator date={msg.createdAt} />}
                  <ChatBubble
                    body={msg.body}
                    imageUrl={msg.imageUrl}
                    fromUser={msg.fromUser}
                    senderName={msg.senderName}
                    avatarConfig={msg.fromUser ? userData?.avatarConfig : undefined}
                    avatarUrl={msg.fromUser ? clerkUser?.imageUrl : undefined}
                    avatarEmail={msg.fromUser ? (clerkUser?.primaryEmailAddress?.emailAddress || '') : undefined}
                    dmSenderAvatarUrl={!msg.fromUser && isDM ? ticket.senderAvatarUrl : undefined}
                    time={formatTime(msg.createdAt)}
                    showSender={isFirstInGroup}
                    compact={!isFirstInGroup}
                  />
                </div>
              );
            })}
          </div>

          {/* Resolved notice */}
          {ticket.status === 'RESOLVED' && (
            <div className="mt-5 flex items-center justify-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5">
              <FiCheck className="h-4 w-4 text-emerald-600 shrink-0" />
              <p className="text-xs text-emerald-700">
                This request is resolved. Send a message if you still need help.
              </p>
            </div>
          )}

        </div>
      </div>

      {/* Compose bar */}
      <div className="shrink-0 border-t border-slate-200/60 bg-white px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-2xl">
          {sendReply.isError && (
            <p className="mb-2 text-xs text-red-600">Failed to send. Please try again.</p>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setSelectedImage(file);
              e.target.value = '';
            }}
          />

          {/* Image preview */}
          {imagePreview && (
            <div className="mb-3 relative inline-block">
              <img src={imagePreview} alt="Attached" className="max-h-36 rounded-xl border border-slate-200/60" />
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white hover:bg-slate-900 cursor-pointer shadow-sm"
                aria-label="Remove image"
              >
                <FiX className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Input row */}
          <div className="flex items-end gap-2.5">
            <div className="flex-1 flex items-end rounded-xl border border-slate-200 bg-slate-50/60 transition-all focus-within:bg-white focus-within:border-violet-300 focus-within:ring-2 focus-within:ring-violet-100">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex h-10 w-10 shrink-0 items-center justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer ml-1"
                aria-label="Attach image"
              >
                <FiImage className="h-[18px] w-[18px]" />
              </button>
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                rows={1}
                className="flex-1 resize-none bg-transparent px-2 py-2.5 text-base text-slate-900 placeholder-slate-400 outline-none"
                style={{ height: 'auto', minHeight: '40px', maxHeight: '120px' }}
              />
            </div>
            <button
              type="button"
              onClick={handleSend}
              disabled={(!message.trim() && !selectedImage) || sendReply.isPending}
              className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl bg-violet-600 px-4 text-sm font-medium text-white transition-colors hover:bg-violet-700 disabled:opacity-30 disabled:hover:bg-violet-600 cursor-pointer"
              aria-label="Send message"
            >
              {sendReply.isPending ? (
                <FiLoader className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Send
                  <FiSend className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Date separator ──────────────────────────────────────────

function DateSeparator({ date }: { date: string }) {
  const d = new Date(date);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();

  let label: string;
  if (isToday) label = 'Today';
  else if (isYesterday) label = 'Yesterday';
  else
    label = d.toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });

  return (
    <div className="flex items-center gap-3 py-3">
      <div className="h-px flex-1 bg-slate-200/60" />
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
        {label}
      </span>
      <div className="h-px flex-1 bg-slate-200/60" />
    </div>
  );
}

// ── Chat bubble ─────────────────────────────────────────────

function ChatBubble({
  body,
  imageUrl,
  fromUser,
  senderName,
  avatarConfig,
  avatarUrl,
  avatarEmail,
  dmSenderAvatarUrl,
  time,
  showSender,
  compact,
}: {
  body: string;
  imageUrl?: string | null;
  fromUser: boolean;
  senderName: string;
  avatarConfig?: Record<string, any> | null;
  avatarUrl?: string | null;
  avatarEmail?: string;
  dmSenderAvatarUrl?: string | null;
  time: string;
  showSender: boolean;
  compact: boolean;
}) {

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
      className={`flex ${fromUser ? 'justify-end' : 'justify-start'} ${compact ? 'mt-1' : 'mt-3'}`}
    >
      {/* Left: support/DM sender avatar */}
      {!fromUser && (
        <div className="shrink-0 mr-2 self-end mb-[22px]">
          {showSender ? (
            dmSenderAvatarUrl
              ? <img src={dmSenderAvatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
              : <img src={gLogoViolet} alt="GranGuard" className="h-8 w-8" />
          ) : <div className="w-8" />}
        </div>
      )}

      <div className={`flex flex-col ${fromUser ? 'items-end' : 'items-start'} max-w-[80%] sm:max-w-[70%]`}>
        {/* Sender name — only for support */}
        {showSender && !fromUser && (
          <p className="text-sm font-medium text-slate-500 mb-1 ml-0.5">{senderName}</p>
        )}
        {imageUrl && (
          <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="block mb-1">
            <img
              src={imageUrl}
              alt="Attached image"
              className="max-w-[280px] max-h-[240px] rounded-2xl border border-slate-200/60 object-cover cursor-pointer hover:opacity-90 transition-opacity"
            />
          </a>
        )}
        {body && (
          <div
            className={`rounded-2xl px-4 py-3 text-[17px] leading-relaxed whitespace-pre-wrap break-words ${
              fromUser
                ? 'bg-violet-600 text-white rounded-tr-md'
                : 'bg-[#f0f0f0] text-slate-800 rounded-tl-md'
            }`}
          >
            <LinkifiedText text={body} isUserMessage={fromUser} />
          </div>
        )}
        <p className={`mt-0.5 text-[11px] text-slate-400 ${fromUser ? 'mr-1' : 'ml-0.5'}`}>
          {time}
        </p>
      </div>

      {/* Right: user avatar */}
      {fromUser && (
        <div className="shrink-0 ml-2 self-end mb-[22px]">
          {showSender ? (
            <AvatarDisplay
              email={avatarEmail || ''}
              avatarConfig={avatarConfig}
              imageUrl={avatarUrl}
              size={32}
            />
          ) : <div className="w-8" />}
        </div>
      )}
    </motion.div>
  );
}
